import { useFormik } from 'formik';
// mantine
import {
    Button,
    Checkbox,
    Collapse,
    Divider,
    FileInput,
    UnstyledButton
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// icons
import { Download, Ellipsis } from 'lucide-react';
// utils
import { getBrowserGMTTimezone } from '@newo/toolkit/datetime';
import { download, getValidFilename } from '@newo/toolkit/file';
import {
    useSuperAgentExportQuery,
    useSuperAgentExportV1Query,
    useSuperAgentImportMutation,
    useSuperAgentImportV1Mutation
} from '@/services/init/init.queries.ts';
import validationSchema from '@/modules/SettingsDrawer/components/ImportAgentForm/ImportAgentForm.schema.ts';
import { tzDatetime } from '@/modules/DateTime/utils';
import { isWindows } from '@/utils';
import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';

interface ImportAgentFormProps {
    onSubmit: () => void;
    projectId: string;
    isV2Enabled: boolean;
}

type AgentData = {
    file: File | undefined;
    check_flow_publication: boolean;
    delete_agent_personas_not_from_import: boolean;
    clear_agent_flows_of_agent_personas_from_import: boolean;
    overwrite_webhooks: boolean;
    clear_customer_attributes: boolean;
};

const ImportAgentForm = ({
    onSubmit,
    projectId,
    isV2Enabled
}: ImportAgentFormProps) => {
    const [isOpened, { toggle }] = useDisclosure(false);
    const { data: profile } = useProfileQuery();
    const { mutate: importSuperAgent, isPending } =
        useSuperAgentImportMutation();
    const { mutateAsync: importSuperAgentV1, isPending: isV1Pending } =
        useSuperAgentImportV1Mutation();

    const initialValues: AgentData = {
        file: undefined,
        check_flow_publication: true,
        delete_agent_personas_not_from_import: true,
        clear_agent_flows_of_agent_personas_from_import: true,
        overwrite_webhooks: false,
        clear_customer_attributes: true
    };

    const handleBackupExport = (result: string | null) => {
        if (result && profile) {
            const parts: [string, string | null, string, string] = [
                profile.idn,
                profile.organization_name,
                tzDatetime().toFormat('yyyy-LL-dd-HH-mm-ss-SSS'),
                getBrowserGMTTimezone()
            ];
            const filename = `backup_${getValidFilename(parts)}.zip`;
            download(
                result,
                filename,
                isWindows()
                    ? 'application/x-zip-compressed'
                    : 'application/zip;'
            );
        }
    };
    const { mutateAsync: backupMutateAsync, isPending: isBackupV2Pending } =
        useSuperAgentExportQuery(handleBackupExport);
    const { mutateAsync: backupMutateAsyncV1, isPending: isBackupV1Pending } =
        useSuperAgentExportV1Query(handleBackupExport, projectId);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (data) => {
            const backupFormData = new FormData();
            backupFormData.append('export_akb', 'true');
            backupFormData.append('export_customer_attributes', 'true');

            const formData = new FormData();

            if (data.file) {
                formData.append('file', data.file);
            }

            formData.append(
                'check_flow_publication',
                String(data.check_flow_publication)
            );
            formData.append(
                'delete_agent_personas_not_from_import',
                String(data.delete_agent_personas_not_from_import)
            );
            formData.append(
                'clear_agent_flows_of_agent_personas_from_import',
                String(data.clear_agent_flows_of_agent_personas_from_import)
            );
            formData.append(
                'overwrite_webhooks',
                String(data.overwrite_webhooks)
            );
            formData.append(
                'clear_customer_attributes',
                String(data.clear_customer_attributes)
            );

            if (isV2Enabled) {
                await backupMutateAsync(backupFormData);
                importSuperAgent(formData, {
                    onSuccess: () => {
                        onSubmit();
                    },
                    onSettled: () => {
                        formik.setSubmitting(false);
                    }
                });
                return;
            }

            await backupMutateAsyncV1();

            importSuperAgentV1(
                { data: formData, projectId },
                {
                    onSuccess: () => {
                        onSubmit();
                    },
                    onSettled: () => {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const isImportInProgress =
        isPending || isV1Pending || isBackupV2Pending || isBackupV1Pending;
    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-4"
            onSubmit={formik.handleSubmit}
        >
            <FileInput
                accept=".zip"
                error={formik.errors.file}
                leftSectionPointerEvents="none"
                name="file"
                placeholder="Choose file (.zip)"
                rightSection={<Ellipsis />}
                rightSectionPointerEvents="none"
                onChange={async (file) => formik.setFieldValue('file', file)}
            />

            <UnstyledButton className="text-xs text-blue-6" onClick={toggle}>
                Advanced settings
            </UnstyledButton>

            <Collapse in={isOpened}>
                <div className="flex flex-col gap-3">
                    <Checkbox
                        checked={formik.values.check_flow_publication}
                        description="If true, service returns error and stops import process in case of failed Flow publication"
                        label="Stop initialization if Flow publication fails"
                        name="check_flow_publication"
                        onChange={formik.handleChange}
                    />
                    <Checkbox
                        checked={
                            formik.values
                                .clear_agent_flows_of_agent_personas_from_import
                        }
                        description="If true, all Agent Flows of Agent Personas specified in imported Flows will be deleted before importing new ones"
                        label="Clear Agent Flows before importing"
                        name="clear_agent_flows_of_agent_personas_from_import"
                        onChange={formik.handleChange}
                    />
                    <Checkbox
                        checked={
                            formik.values.delete_agent_personas_not_from_import
                        }
                        description="If true, all Agent Personas not specified in imported Flows will be deleted"
                        label="Delete Agent Personas not included in imported Flows"
                        name="delete_agent_personas_not_from_import"
                        onChange={formik.handleChange}
                    />
                    <Checkbox
                        checked={formik.values.clear_customer_attributes}
                        description="If true, all Customer Attributes will be deleted before importing new ones"
                        label="Clear Customer Attributes"
                        name="clear_customer_attributes"
                        onChange={formik.handleChange}
                    />
                    <Divider className="my-4" />
                    <Checkbox
                        checked={formik.values.overwrite_webhooks}
                        description="If true, existing WebHooks will be overwritten"
                        label="Overwrite Webhooks"
                        name="overwrite_webhooks"
                        onChange={formik.handleChange}
                    />
                </div>
            </Collapse>

            <div>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    leftSection={<Download />}
                    loading={isImportInProgress}
                    size="sm"
                    type="submit"
                >
                    Import
                </Button>
            </div>
        </form>
    );
};

export default ImportAgentForm;
